import { useFocusWithin } from '../interactions';
import { filterDOMProps, mergeProps, useId } from '../utils';
import { useSelectableList } from '../selection';
import { useLabel } from '../label';
import { listData } from './utils';
/**
 * Provides the behavior and accessibility implementation for a listbox component.
 * A listbox displays a list of options and allows a user to select one or more of them.
 * @param props - Props for the listbox.
 * @param state - State for the listbox, as returned by `useListState`.
 */ export function useListBox(props, state, ref) {
    const domProps = filterDOMProps(props, {
        labelable: true
    });
    // Use props instead of state here. We don't want this to change due to long press.
    const selectionBehavior = props.selectionBehavior || 'toggle';
    let linkBehavior = props.linkBehavior || (selectionBehavior === 'replace' ? 'action' : 'override');
    if (selectionBehavior === 'toggle' && linkBehavior === 'action') {
        // linkBehavior="action" does not work with selectionBehavior="toggle" because there is no way
        // to initiate selection (checkboxes are not allowed inside a listbox). Link items will not be
        // selectable in this configuration.
        linkBehavior = 'override';
    }
    const { listProps } = useSelectableList({
        ...props,
        ref,
        selectionManager: state.selectionManager,
        collection: state.collection,
        disabledKeys: state.disabledKeys,
        linkBehavior
    });
    const { focusWithinProps } = useFocusWithin({
        onFocusWithin: props.onFocus,
        onBlurWithin: props.onBlur,
        onFocusWithinChange: props.onFocusChange
    });
    // Share list id and some props with child options.
    const id = useId(props.id);
    listData.set(state, {
        id,
        shouldUseVirtualFocus: props.shouldUseVirtualFocus,
        shouldSelectOnPressUp: props.shouldSelectOnPressUp,
        shouldFocusOnHover: props.shouldFocusOnHover,
        isVirtualized: props.isVirtualized,
        onAction: props.onAction,
        linkBehavior
    });
    const { labelProps, fieldProps } = useLabel({
        ...props,
        id,
        // listbox is not an HTML input element so it
        // shouldn't be labeled by a <label> element.
        labelElementType: 'span'
    });
    return {
        labelProps,
        listBoxProps: mergeProps(domProps, focusWithinProps, state.selectionManager.selectionMode === 'multiple' ? {
            'aria-multiselectable': 'true'
        } : {}, {
            role: 'listbox',
            ...mergeProps(fieldProps, listProps)
        })
    };
}

//# sourceMappingURL=useListBox.js.map